import { useContext, useMemo, useState } from 'react';
import { Box, Button, Heading, Modal, Paragraph } from '@zillow/constellation-10';
import { RecurringFrequency } from '@zg-rentals/ts-z-calendar-client';

import RecuringAvailabilityConfirmationModal from '../RecuringAvailabilityConfirmationModal';
import EventDetails from '../../../EventDetails';
import { type AvailabilityEvent } from '../../../../constants/TourCalendar';
import { getDailyRecurrenceString, getTourDateAndTime, getWeeklyRecurrenceString } from '../../../../utils/formatTime';

import TourCalendarContext from '../../../TourCalendarWithSettings/context/TourCalendarContext';

const getRecurringDaysSection = (event: AvailabilityEvent) => {
  const isDailyRecurringEvent = event.isRecurringEvent && event.frequency === RecurringFrequency.DAILY;
  const isWeeklyRecurringEvent =
    event.isRecurringEvent && event.frequency === RecurringFrequency.WEEKLY && event.recurringDays;

  if (isDailyRecurringEvent || isWeeklyRecurringEvent) {
    const recurrenceEndDateMs = event.recurrenceEndDateMs;
    const recurrenceString = isDailyRecurringEvent
      ? getDailyRecurrenceString(recurrenceEndDateMs)
      : // @ts-expect-error we're validating recurring days in isWeeklyRecurringEvent
        getWeeklyRecurrenceString(event.recurringDays, recurrenceEndDateMs);

    return (
      <div>
        <Heading data-testid="AvailabilityEventPreviewModal-Repeat" level={6}>
          Repeat
        </Heading>
        <Paragraph>{recurrenceString}</Paragraph>
      </div>
    );
  }

  return null;
};

type AvailabilityEventPreviewModalProps = {
  children?: JSX.Element;
  event: AvailabilityEvent;
  header: React.ReactNode;
  isOpen?: boolean;
  onClose: () => void;
  onDeleteClick: () => void;
  onEditClick: () => void;
};

const AvailabilityEventPreviewModal = ({
  children,
  event,
  header,
  isOpen,
  onClose,
  onDeleteClick,
  onEditClick,
}: AvailabilityEventPreviewModalProps) => {
  const { listingAddress, propertyPhotoUrl } = useContext(TourCalendarContext);
  const [displayConfirmationModal, setDisplayConfirmationModal] = useState(false);
  const eventDateAndTime = useMemo(() => getTourDateAndTime(event.start, event.end), [event]);

  const handleDeleteClick = () => {
    if (event.isRecurringEvent) {
      return setDisplayConfirmationModal(true);
    }

    onDeleteClick();
  };

  return (
    <>
      <Modal
        aria-label="Availability details"
        body={
          <Box
            data-testid="AvailabilityEventPreviewModal"
            css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}
          >
            <EventDetails
              eventDateAndTime={eventDateAndTime}
              listingStreet={listingAddress || ''}
              photoUrl={propertyPhotoUrl}
              title={event.title as string}
            />
            {getRecurringDaysSection(event)}
          </Box>
        }
        dividers
        footer={
          <Box css={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'default', width: '100%' }}>
            <Button onClick={handleDeleteClick} data-testid="AvailabilityEventPreviewModal-DeleteBtn">
              Delete
            </Button>
            <Button emphasis="filled" onClick={onEditClick} data-testid="AvailabilityEventPreviewModal-EditBtn">
              Edit
            </Button>
          </Box>
        }
        header={header}
        onOpenChange={(open) => !open && onClose()}
        open={isOpen}
        trigger={children}
      />

      {displayConfirmationModal && (
        <RecuringAvailabilityConfirmationModal
          isForDelete={true}
          onClose={() => setDisplayConfirmationModal(false)}
          onConfirm={onDeleteClick}
        />
      )}
    </>
  );
};

export default AvailabilityEventPreviewModal;
